import React, { useEffect, useMemo, useRef, useState } from "react";
import "./AudioRecorder.scss";
import axios from "axios";

type UploadStatus =
  | "idle"
  | "queued"
  | "uploading"
  | "uploaded"
  | "failed"
  | "expired";

type SavedRecording = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  durationSeconds: number;
  durationText: string;
  createdAt: string;
  objectUrl: string;
  uploadStatus: UploadStatus;
  uploadProgress?: number;
  uploadSessionId?: string;
  remoteKey?: string;
  remoteLocation?: string;
  errorMessage?: string;
};

type PersistedRecordingMeta = Omit<SavedRecording, "objectUrl">;

type AudioInputDevice = {
  deviceId: string;
  label: string;
};

type ConfirmAction = "pause" | "resume" | "stop" | "delete" | "clearAll";

type ConfirmModalState = {
  action: ConfirmAction;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  recordingId?: string;
};

type PlayerState = {
  currentTime: number;
  duration: number;
};

type PresignedUrlItem =
  | string
  | {
      url?: string;
      presigned_url?: string;
      upload_url?: string;
      part_number?: number;
      partNumber?: number;
    };

type GenerateUploadResponse = {
  upload_session_id: string;
  part_size: number;
  urls: PresignedUrlItem[];
};

type CompleteUploadResponse = {
  etag?: string;
  ETag?: string;
  location?: string;
  Location?: string;
  key?: string;
  Key?: string;
};

type UploadPartResult = {
  part_number: number;
  etag: string;
  size: number;
};

type PendingUploadJob = {
  recordingId: string;
  consentBundleId: string | number;
  uploadSessionId: string;
  fileName: string;
  mimeType: string;
  size: number;
  durationSeconds: number;
  recordedAt: string;
  partSize: number;
  urls: PresignedUrlItem[];
  attempts: number;
  maxAttempts: number;
  createdAtMs: number;
  expiresAtMs: number;
  lastAttemptAtMs?: number;
  uploadedParts: UploadPartResult[];
  status: "queued" | "uploading" | "failed" | "completed" | "expired";
  lastError?: string;
};

type AudioRecorderProps = {
  consentBundleId: string | number;
  apiBaseUrl?: string;
  maxRecordings?: number;
  retryWindowMinutes?: number;
  maxUploadAttempts?: number;
  retryPollMs?: number;
  authToken?: string;
  onUploadComplete?: (payload: {
    recording: SavedRecording;
    response: CompleteUploadResponse;
  }) => void;
  onUploadFailed?: (payload: {
    recording: SavedRecording;
    error: unknown;
  }) => void;
};

const DB_NAME = "audio_recorder_db";
const DB_VERSION = 1;
const STORE_NAME = "recordings";
const META_STORAGE_KEY = "local_audio_recording_meta_v3";
const PENDING_UPLOADS_KEY = "local_audio_recording_pending_uploads_v1";
const DEFAULT_MAX_RECORDINGS = 2;
const DEFAULT_RETRY_WINDOW_MINUTES = 20;
const DEFAULT_MAX_UPLOAD_ATTEMPTS = 5;
const DEFAULT_RETRY_POLL_MS = 30_000;

function formatTime(totalSeconds: number) {
  const safeSeconds = Number.isFinite(totalSeconds)
    ? Math.max(0, Math.floor(totalSeconds))
    : 0;

  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
}

function getBestMimeType() {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "",
  ];

  for (const type of types) {
    if (!type) return "";
    if (MediaRecorder.isTypeSupported(type)) return type;
  }

  return "";
}

function getFileExtension(mimeType: string, fileName = "") {
  const cleanMimeType = (mimeType || "").toLowerCase();
  const nameExtension = fileName
    .split("?")[0]
    .split("#")[0]
    .split(".")
    .pop()
    ?.toLowerCase();

  if (nameExtension === "webm") return "webm";
  if (nameExtension === "m4a") return "m4a";
  if (nameExtension === "mp4") return "m4a";
  if (nameExtension === "mp3") return "mp3";
  if (nameExtension === "wav") return "wav";

  if (cleanMimeType.includes("webm")) return "webm";
  if (cleanMimeType.includes("mp4")) return "m4a";
  if (cleanMimeType.includes("mpeg")) return "mp3";
  if (cleanMimeType.includes("wav")) return "wav";

  return "webm";
}

function createId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getCookieValue(name: string) {
  if (typeof document === "undefined") return "";

  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return value ? decodeURIComponent(value) : "";
}

function getDefaultAuthToken() {
  if (typeof window === "undefined") return "";

  const possibleKeys = [
    "token",
    "access_token",
    "accessToken",
    "auth_token",
    "authToken",
    "jwt",
  ];

  for (const key of possibleKeys) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }

  return getCookieValue("auth_token") || getCookieValue("token") || "";
}

function normalizeAuthHeader(rawToken?: string | null) {
  if (!rawToken) return "";

  let cleanedToken = String(rawToken).trim();

  if (!cleanedToken) return "";

  if (
    (cleanedToken.startsWith('"') && cleanedToken.endsWith('"')) ||
    (cleanedToken.startsWith("'") && cleanedToken.endsWith("'"))
  ) {
    cleanedToken = cleanedToken.slice(1, -1).trim();
  }

  if (!cleanedToken) return "";

  return cleanedToken.toLowerCase().startsWith("bearer ")
    ? cleanedToken
    : `Bearer ${cleanedToken}`;
}

function getAuthorizationHeader(explicitToken?: string) {
  return normalizeAuthHeader(
    explicitToken ||
      String(axios.defaults.headers.common.Authorization || "") ||
      getDefaultAuthToken()
  );
}

function buildApiUrl(apiBaseUrl: string, path: string) {
  const cleanBase = apiBaseUrl.replace(/\/$/, "");
  return cleanBase ? `${cleanBase}${path}` : path;
}

function getUploadUrl(item: PresignedUrlItem) {
  if (typeof item === "string") return item;
  return item.url || item.presigned_url || item.upload_url || "";
}

function getPartNumber(item: PresignedUrlItem, index: number) {
  if (typeof item === "string") return index + 1;
  return item.part_number || item.partNumber || index + 1;
}

function getReadableFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getStoredRecordingMeta(): PersistedRecordingMeta[] {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read recording metadata:", error);
    return [];
  }
}

function saveRecordingMeta(recordings: SavedRecording[]) {
  const meta: PersistedRecordingMeta[] = recordings.map(
    ({ objectUrl, ...recording }) => recording
  );

  localStorage.setItem(META_STORAGE_KEY, JSON.stringify(meta));
}

function getPendingUploadJobs(): PendingUploadJob[] {
  try {
    const raw = localStorage.getItem(PENDING_UPLOADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read pending uploads:", error);
    return [];
  }
}

function savePendingUploadJobs(jobs: PendingUploadJob[]) {
  localStorage.setItem(PENDING_UPLOADS_KEY, JSON.stringify(jobs));
}

function upsertPendingUploadJob(job: PendingUploadJob) {
  const jobs = getPendingUploadJobs();
  const existingIndex = jobs.findIndex(
    (item) => item.uploadSessionId === job.uploadSessionId
  );

  if (existingIndex >= 0) {
    jobs[existingIndex] = job;
  } else {
    jobs.unshift(job);
  }

  savePendingUploadJobs(jobs);
}

function removePendingUploadJob(uploadSessionId: string) {
  savePendingUploadJobs(
    getPendingUploadJobs().filter(
      (job) => job.uploadSessionId !== uploadSessionId
    )
  );
}

function openAudioDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB is not supported in this browser."));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function saveBlobToDb(id: string, blob: Blob) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.put(blob, id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getBlobFromDb(id: string): Promise<Blob | null> {
  const db = await openAudioDb();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      db.close();
      resolve(request.result || null);
    };

    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

async function deleteBlobFromDb(id: string) {
  const db = await openAudioDb();

  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    store.delete(id);

    transaction.oncomplete = () => {
      db.close();
      resolve();
    };

    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

async function getRecordingFile(recording: SavedRecording) {
  const blob = await getBlobFromDb(recording.id);

  if (!blob) {
    throw new Error("The selected recording could not be found in local storage.");
  }

  const mimeType = recording.mimeType || blob.type || "audio/webm";
  const extension = getFileExtension(mimeType, recording.name);
  const baseName = recording.name
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-z0-9-_]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return new File([blob], `${baseName || "recording"}.${extension}`, {
    type: mimeType,
  });
}

async function getAudioDurationSeconds(file: Blob) {
  return new Promise<number>((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();

    const cleanup = () => URL.revokeObjectURL(url);

    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(Math.round(duration));
    };
    audio.onerror = () => {
      cleanup();
      resolve(0);
    };
    audio.src = url;
  });
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({
  consentBundleId,
  apiBaseUrl = "",
  maxRecordings = DEFAULT_MAX_RECORDINGS,
  retryWindowMinutes = DEFAULT_RETRY_WINDOW_MINUTES,
  maxUploadAttempts = DEFAULT_MAX_UPLOAD_ATTEMPTS,
  retryPollMs = DEFAULT_RETRY_POLL_MS,
  authToken,
  onUploadComplete,
  onUploadFailed,
}) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const fullRecordingPartsRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const recordingSecondsRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const volumeTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const objectUrlsRef = useRef<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [status, setStatus] = useState("Ready to record");
  const [recordings, setRecordings] = useState<SavedRecording[]>([]);
  const [selectedRecordingId, setSelectedRecordingId] = useState("");
  const [audioInputs, setAudioInputs] = useState<AudioInputDevice[]>([]);
  const [selectedMicId, setSelectedMicId] = useState("");
  const [selectedMicLabel, setSelectedMicLabel] = useState("Default microphone");
  const [micLevel, setMicLevel] = useState(0);
  const [isMicPreviewActive, setIsMicPreviewActive] = useState(false);
  const [showMicSettings, setShowMicSettings] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState | null>(
    null
  );
  const [activePlaybackId, setActivePlaybackId] = useState<string | null>(null);
  const [playerStates, setPlayerStates] = useState<Record<string, PlayerState>>(
    {}
  );
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const selectedRecording = useMemo(
    () => recordings.find((recording) => recording.id === selectedRecordingId),
    [recordings, selectedRecordingId]
  );

  const hasLiveInput = isMicPreviewActive && micLevel >= 3;
  const hasNoMicInput = isMicPreviewActive && !hasLiveInput;

  const getRequestHeaders = (json = true) => {
    const authorization = getAuthorizationHeader(authToken);
    const headers: Record<string, string> = {};

    if (json) headers["Content-Type"] = "application/json";
    if (authorization) headers.Authorization = authorization;

    return headers;
  };

  const getAudioConstraints = (
    deviceId = selectedMicId
  ): MediaStreamConstraints => ({
    audio: deviceId
      ? {
          deviceId: { exact: deviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        }
      : {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
  });

  const syncRecordings = (nextRecordings: SavedRecording[]) => {
    setRecordings(nextRecordings);
    saveRecordingMeta(nextRecordings);
  };

  const patchRecording = (id: string, patch: Partial<SavedRecording>) => {
    setRecordings((prev) => {
      const next = prev.map((recording) =>
        recording.id === id ? { ...recording, ...patch } : recording
      );
      saveRecordingMeta(next);
      return next;
    });
  };

  const stopTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = () => {
    stopTimer();

    timerRef.current = window.setInterval(() => {
      recordingSecondsRef.current += 1;
      setRecordingSeconds(recordingSecondsRef.current);
    }, 1000);
  };

  const resetTimer = () => {
    stopTimer();
    recordingSecondsRef.current = 0;
    setRecordingSeconds(0);
  };

  const stopMicLevelMonitor = () => {
    if (volumeTimerRef.current) {
      window.clearInterval(volumeTimerRef.current);
      volumeTimerRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    setMicLevel(0);
  };

  const stopPreviewStream = () => {
    previewStreamRef.current?.getTracks().forEach((track) => track.stop());
    previewStreamRef.current = null;
    setIsMicPreviewActive(false);
  };

  const startMicLevelMonitor = (stream: MediaStream) => {
    stopMicLevelMonitor();

    const audioContext = new AudioContext();

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => undefined);
    }

    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);

    audioContextRef.current = audioContext;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    volumeTimerRef.current = window.setInterval(() => {
      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => undefined);
      }

      analyser.getByteFrequencyData(dataArray);

      const average =
        dataArray.reduce((total, value) => total + value, 0) / dataArray.length;
      const noiseFloor = 5;
      const normalized = average <= noiseFloor ? 0 : average - noiseFloor;
      const nextLevel = Math.min(100, Math.round(normalized * 2.5));

      setMicLevel(nextLevel);
    }, 90);
  };

  const startMicPreview = async (deviceId = selectedMicId) => {
    try {
      if (isRecording || !navigator.mediaDevices?.getUserMedia) return;

      stopPreviewStream();
      stopMicLevelMonitor();

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(deviceId)
      );

      previewStreamRef.current = stream;
      setIsMicPreviewActive(true);
      startMicLevelMonitor(stream);
      setStatus("Live mic preview ready");
    } catch (error) {
      console.error("Failed to start live mic preview:", error);
      setIsMicPreviewActive(false);
      setMicLevel(0);
      setStatus("Live mic preview unavailable");
    }
  };

  const loadAudioInputDevices = async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setStatus("Microphone device selection is not supported");
        return;
      }

      let permissionStream: MediaStream | null = null;

      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
      } catch {
        // Browser may still return generic microphone labels.
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices
        .filter((device) => device.kind === "audioinput")
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        }));

      setAudioInputs(microphones);

      if (!selectedMicId && microphones.length > 0) {
        setSelectedMicId(microphones[0].deviceId);
        setSelectedMicLabel(microphones[0].label);
        window.setTimeout(() => startMicPreview(microphones[0].deviceId), 150);
      }

      if (selectedMicId) {
        const selectedMic = microphones.find(
          (mic) => mic.deviceId === selectedMicId
        );

        if (selectedMic) setSelectedMicLabel(selectedMic.label);
      }

      if (microphones.length === 0) {
        setSelectedMicLabel("No microphone found");
        setIsMicPreviewActive(false);
      }
    } catch (error) {
      console.error("Failed to load microphones:", error);
      setStatus("Could not load microphones");
    }
  };

  const handleMicChange = (deviceId: string) => {
    setSelectedMicId(deviceId);

    const selectedMic = audioInputs.find((mic) => mic.deviceId === deviceId);
    setSelectedMicLabel(selectedMic?.label || "Selected microphone");

    if (!isRecording) {
      window.setTimeout(() => startMicPreview(deviceId), 80);
    }
  };

  const triggerSavedFlash = () => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2200);
  };

  const addSavedRecordingFromBlob = async (blob: Blob, options?: { fileName?: string; durationSeconds?: number }) => {
    const createdAt = new Date();
    const id = createId();
    const objectUrl = URL.createObjectURL(blob);
    objectUrlsRef.current.push(objectUrl);

    const durationSeconds =
      options?.durationSeconds ?? Math.round(await getAudioDurationSeconds(blob));
    const extension = getFileExtension(blob.type || "audio/webm", options?.fileName);
    const cleanProvidedName = options?.fileName?.trim();

    const newRecording: SavedRecording = {
      id,
      name:
        cleanProvidedName ||
        `Recording ${createdAt.toLocaleString()}.${extension}`,
      mimeType: blob.type || "audio/webm",
      size: blob.size,
      durationSeconds,
      durationText: formatTime(durationSeconds),
      createdAt: createdAt.toISOString(),
      objectUrl,
      uploadStatus: "idle",
      uploadProgress: 0,
    };

    await saveBlobToDb(id, blob);

    const updatedRecordings = [newRecording, ...recordings].slice(0, maxRecordings);
    const removedRecordings = [newRecording, ...recordings].slice(maxRecordings);

    await Promise.all(
      removedRecordings.map(async (recording) => {
        await deleteBlobFromDb(recording.id).catch(() => undefined);
        if (recording.objectUrl) URL.revokeObjectURL(recording.objectUrl);
      })
    );

    syncRecordings(updatedRecordings);
    setSelectedRecordingId(newRecording.id);
    triggerSavedFlash();
    setStatus("Audio saved locally. Select it and press Upload audio.");
  };

function formatDateTimeWithTimezone(isoDate: string) {
  const date = new Date(isoDate);
  const pad = (value: number) => String(value).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absoluteOffset / 60));
  const offsetMins = pad(absoluteOffset % 60);
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
}

function bytesToMegabytes(bytes: number) {
  return Number((bytes / (1024 * 1024)).toFixed(4));
}


const getUploadFileName = (file?: Blob) => {
  const mimeType = file?.type || "audio/webm";
  const extension = getFileExtension(mimeType);

  return `ConsentbundleID ${consentBundleId}.${extension}`;
};

const generateUploadUrls = async (recording: SavedRecording, file: File) => {
  const authorization = getAuthorizationHeader(authToken);
  const fileName = getUploadFileName(file);
  const mimeType = file.type || recording.mimeType || "audio/webm";

  const params = {
    file_name: fileName,
    file_size: bytesToMegabytes(file.size),
    duration: Math.round(recording.durationSeconds || 0),
    recorded_at: formatDateTimeWithTimezone(recording.createdAt),
    mime_type: mimeType,
  };

  const headers: Record<string, string> = {};

  if (authorization) {
    headers.Authorization = authorization;
  }

  try {
    const { data } = await axios.post<GenerateUploadResponse>(
      buildApiUrl(
        apiBaseUrl,
        `/audio-recording/generate_upload_url/${consentBundleId}`
      ),
      null,
      {
        params,
        withCredentials: false,
        headers,
      }
    );

    console.log("Generate upload URL response:", data);

    if (
      !data.upload_session_id ||
      !data.part_size ||
      !Array.isArray(data.urls)
    ) {
      throw new Error("Invalid generate_upload_url response from backend.");
    }

    if (data.urls.length === 0) {
      throw new Error("Backend returned no presigned upload URLs.");
    }

    return data;
  } catch (error: any) {
    console.error("Generate upload URL axios error:", {
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      response: error?.response?.data,
      request: error?.request,
    });

    throw new Error(
      `Generate upload URL failed${
        error?.response?.status ? ` with status ${error.response.status}` : ""
      }${
        error?.response?.data
          ? `: ${
              typeof error.response.data === "string"
                ? error.response.data
                : JSON.stringify(error.response.data)
            }`
          : error?.message
          ? `: ${error.message}`
          : ""
      }`
    );
  }
};

const generateCustomUploadUrls = async (
  uploadSessionId: string,
  partNumbers: number[]
): Promise<GenerateUploadResponse> => {
  const authorization = getAuthorizationHeader(authToken);

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (authorization) {
      headers.Authorization = authorization;
    }

    const { data } = await axios.post<GenerateUploadResponse>(
      buildApiUrl(
        apiBaseUrl,
        `/audio-recording/generate_custom_upload_url/${uploadSessionId}`
      ),
      {
        part_numbers: partNumbers,
      },
      {
        withCredentials: false,
        headers,
      }
    );

    if (
      !data.upload_session_id ||
      !data.part_size ||
      !Array.isArray(data.urls)
    ) {
      throw new Error("Invalid generate_custom_upload_url response from backend.");
    }

    return data;
  } catch (error: any) {
    const status = error?.response?.status;
    const responseData = error?.response?.data;

    throw new Error(
      `Generate custom upload URL failed${
        status ? ` with status ${status}` : ""
      }${
        responseData
          ? `: ${
              typeof responseData === "string"
                ? responseData
                : JSON.stringify(responseData)
            }`
          : error?.message
          ? `: ${error.message}`
          : ""
      }`
    );
  }
};

const uploadOnePart = async (
  url: string,
  partBlob: Blob,
  partNumber: number,
  mimeType: string
): Promise<UploadPartResult> => {
  const response = await fetch(url, {
    method: "PUT",
    body: partBlob,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Part ${partNumber} upload failed with status ${response.status}${
        errorText ? `: ${errorText}` : ""
      }`
    );
  }

  const etag = response.headers.get("ETag") || response.headers.get("etag") || "";

  if (!etag) {
    throw new Error(
      `Part ${partNumber} uploaded, but S3 did not expose the ETag header. Add ETag to S3 CORS ExposeHeaders.`
    );
  }

  return {
    part_number: partNumber,
    etag: etag.replaceAll('"', ""),
    size: partBlob.size,
  };
}; 

const uploadAudioPartsToS3 = async (
  file: File | Blob,
  generateResponse: GenerateUploadResponse,
  onProgress?: (progress: number) => void
): Promise<UploadPartResult[]> => {
  const uploadedParts: UploadPartResult[] = [];

  const partSize = generateResponse.part_size;
  const totalParts = generateResponse.urls.length;

  for (let index = 0; index < generateResponse.urls.length; index += 1) {
    const urlItem = generateResponse.urls[index];

    const uploadUrl = getUploadUrl(urlItem);
    const partNumber = getPartNumber(urlItem, index);

    if (!uploadUrl) {
      throw new Error(`Missing upload URL for part ${partNumber}`);
    }

    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const partBlob = file.slice(start, end);

    console.log("Uploading S3 part", {
      partNumber,
      start,
      end,
      chunkSize: partBlob.size,
      fileSize: file.size,
      partSize,
    });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: partBlob,
      credentials: "omit",
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(
        `Part ${partNumber} upload failed with status ${response.status}${
          errorText ? `: ${errorText}` : ""
        }`
      );
    }

    const etag = response.headers.get("ETag") || response.headers.get("etag");

    if (!etag) {
      throw new Error(
        `Part ${partNumber} uploaded, but ETag is missing. S3 CORS must expose ETag.`
      );
    }

    uploadedParts.push({
      part_number: partNumber,
      etag: etag.replaceAll('"', ""),
      size: partBlob.size,
    });

    onProgress?.(Math.round((uploadedParts.length / totalParts) * 100));
  }

  return uploadedParts.sort((a, b) => a.part_number - b.part_number);
};


const uploadRecordingWithPresignedUrls = async (recording: SavedRecording) => {
  try {
    setStatus("Requesting presigned upload URLs...");

    patchRecording(recording.id, {
      uploadStatus: "uploading",
      uploadProgress: 0,
      errorMessage: undefined,
    });

    const audioFile = await getRecordingFile(recording);

    const generateResponse = await generateUploadUrls(recording, audioFile);

    setStatus("Uploading audio parts to S3...");

    const uploadedParts = await uploadAudioPartsToS3(
      audioFile,
      generateResponse,
      (progress) => {
        patchRecording(recording.id, {
          uploadProgress: progress,
        });
      }
    );

    setStatus("Completing upload session...");

    const completeResponse = await completeUpload(
      {
        recordingId: recording.id,
        consentBundleId,
        uploadSessionId: generateResponse.upload_session_id,
        fileName: getUploadFileName(audioFile),
        mimeType: audioFile.type || recording.mimeType,
        size: audioFile.size,
        durationSeconds: recording.durationSeconds,
        recordedAt: recording.createdAt,
        partSize: generateResponse.part_size,
        urls: generateResponse.urls,
        attempts: 1,
        maxAttempts: maxUploadAttempts,
        createdAtMs: Date.now(),
        expiresAtMs: Date.now() + retryWindowMinutes * 60 * 1000,
        uploadedParts,
        status: "uploading",
      },
      uploadedParts
    );

    patchRecording(recording.id, {
      uploadStatus: "uploaded",
      uploadProgress: 100,
      remoteKey: completeResponse.key || completeResponse.Key,
      remoteLocation: completeResponse.location || completeResponse.Location,
      errorMessage: undefined,
    });

    setStatus("Audio upload completed.");
  } catch (error: any) {
    console.error(error);

    const message = error?.message || "Audio upload failed.";

    patchRecording(recording.id, {
      uploadStatus: "failed",
      errorMessage: message,
    });

    setStatus(message);
    onUploadFailed?.({ recording, error });
  }
};

const completeUpload = async (
  job: PendingUploadJob,
  parts: UploadPartResult[]
): Promise<CompleteUploadResponse> => {
  const authorization = getAuthorizationHeader(authToken);

  const cleanParts = parts
    .filter((part) => part.etag)
    .sort((a, b) => a.part_number - b.part_number)
    .map((part) => ({
      PartNumber: part.part_number,
      ETag: part.etag,
    }));

  if (cleanParts.length !== job.urls.length) {
    throw new Error(
      `Upload complete blocked: expected ${job.urls.length} uploaded parts, but got ${cleanParts.length}.`
    );
  }

  const payload = {
    parts: uploadedParts.map((part) => ({
      PartNumber: part.part_number,
      ETag: part.etag,
    })),
    file_size: bytesToMegabytes(file.size),
    mime_type: file.type || "audio/webm",
    file_name: `ConsentbundleID ${consentBundleId}.webm`,
  };

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (authorization) {
    headers.Authorization = authorization;
  }

  try {
    const { data } = await axios.post<CompleteUploadResponse>(
      buildApiUrl(
        apiBaseUrl,
        `/audio-recording/upload-complete/${job.uploadSessionId}`
      ),
      payload,
      {
        withCredentials: false,
        headers,
      }
    );

    console.log("Upload complete response:", data);
    return data;
  } catch (error: any) {
    console.error("Upload complete axios error:", {
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      response: error?.response?.data,
      request: error?.request,
    });

    throw new Error(
      `Upload complete failed${
        error?.response?.status ? ` with status ${error.response.status}` : ""
      }${
        error?.response?.data
          ? `: ${
              typeof error.response.data === "string"
                ? error.response.data
                : JSON.stringify(error.response.data)
            }`
          : error?.message
          ? `: ${error.message}`
          : ""
      }`
    );
  }
};

const runUploadJob = async (job: PendingUploadJob) => {
    const now = Date.now();

    if (now > job.expiresAtMs) {
      const expiredJob: PendingUploadJob = {
        ...job,
        status: "expired",
        lastError: "Upload session expired after retry window.",
      };
      upsertPendingUploadJob(expiredJob);
      patchRecording(job.recordingId, {
        uploadStatus: "expired",
        errorMessage: expiredJob.lastError,
      });
      return;
    }

    if (job.attempts >= job.maxAttempts) {
      const failedJob: PendingUploadJob = {
        ...job,
        status: "failed",
        lastError: "Maximum upload retry attempts reached.",
      };
      upsertPendingUploadJob(failedJob);
      patchRecording(job.recordingId, {
        uploadStatus: "failed",
        errorMessage: failedJob.lastError,
      });
      return;
    }

    const blob = await getBlobFromDb(job.recordingId);
    if (!blob) {
      throw new Error("Local audio blob not found for retry upload.");
    }

    let workingJob: PendingUploadJob = {
      ...job,
      attempts: job.attempts + 1,
      lastAttemptAtMs: now,
      status: "uploading",
      lastError: undefined,
    };

    upsertPendingUploadJob(workingJob);
    patchRecording(job.recordingId, {
      uploadStatus: "uploading",
      uploadSessionId: job.uploadSessionId,
      errorMessage: undefined,
    });

    const uploadedParts = [...workingJob.uploadedParts];
    const uploadedPartNumbers = new Set(uploadedParts.map((part) => part.part_number));

    for (let index = 0; index < workingJob.urls.length; index += 1) {
      const urlItem = workingJob.urls[index];
      const uploadUrl = getUploadUrl(urlItem);
      const partNumber = getPartNumber(urlItem, index);

      if (!uploadUrl) {
        throw new Error(`Missing presigned URL for part ${partNumber}.`);
      }

      if (uploadedPartNumbers.has(partNumber)) {
        continue;
      }

      const start = (partNumber - 1) * workingJob.partSize;
      const end = Math.min(start + workingJob.partSize, blob.size);
      const partBlob = blob.slice(start, end, workingJob.mimeType);

      const result = await uploadOnePart(
        uploadUrl,
        partBlob,
        partNumber,
        workingJob.mimeType
      );

      uploadedParts.push(result);
      uploadedPartNumbers.add(partNumber);

      const progress = Math.round((uploadedParts.length / workingJob.urls.length) * 100);
      patchRecording(job.recordingId, { uploadProgress: progress });

      workingJob = {
        ...workingJob,
        uploadedParts,
      };
      upsertPendingUploadJob(workingJob);
    }

    const completeResponse = await completeUpload(workingJob, uploadedParts);

    removePendingUploadJob(workingJob.uploadSessionId);
    patchRecording(job.recordingId, {
      uploadStatus: "uploaded",
      uploadProgress: 100,
      remoteKey: completeResponse.key || completeResponse.Key,
      remoteLocation: completeResponse.location || completeResponse.Location,
      errorMessage: undefined,
    });

    const uploadedRecording = recordings.find((item) => item.id === job.recordingId);
    if (uploadedRecording) {
      onUploadComplete?.({
        recording: {
          ...uploadedRecording,
          uploadStatus: "uploaded",
          uploadProgress: 100,
          remoteKey: completeResponse.key || completeResponse.Key,
          remoteLocation: completeResponse.location || completeResponse.Location,
        },
        response: completeResponse,
      });
    }

    setStatus("Audio upload completed.");
  };



// depricated
  const queueUploadRetry = (
    recording: SavedRecording,
    file: File,
    generateResponse: GenerateUploadResponse
  ) => {
    const now = Date.now();
    const job: PendingUploadJob = {
      recordingId: recording.id,
      consentBundleId,
      uploadSessionId: generateResponse.upload_session_id,
      fileName: file.name,
      mimeType: file.type || recording.mimeType,
      size: file.size,
      durationSeconds: recording.durationSeconds,
      recordedAt: recording.createdAt,
      partSize: generateResponse.part_size,
      urls: generateResponse.urls,
      attempts: 0,
      maxAttempts: maxUploadAttempts,
      createdAtMs: now,
      expiresAtMs: now + retryWindowMinutes * 60 * 1000,
      uploadedParts: [],
      status: "queued",
    };

    upsertPendingUploadJob(job);
    patchRecording(recording.id, {
      uploadStatus: "queued",
      uploadSessionId: job.uploadSessionId,
      uploadProgress: 0,
    });

    return job;
  };

  const retryPendingUploads = async () => {
  const jobs = getPendingUploadJobs().filter(
    (job) =>
      (job.status === "queued" ||
        job.status === "failed" ||
        job.status === "uploading") &&
      Date.now() <= job.expiresAtMs
  );

  for (const job of jobs) {
    try {
      if (job.attempts >= job.maxAttempts) {
        const errorMessage = "Maximum upload retry attempts reached.";

        const failedJob: PendingUploadJob = {
          ...job,
          status: "failed",
          lastAttemptAtMs: Date.now(),
          lastError: errorMessage,
        };

        upsertPendingUploadJob(failedJob);

        patchRecording(job.recordingId, {
          uploadStatus: "failed",
          errorMessage,
        });

        try {
          await abortUpload(failedJob, errorMessage);
          removePendingUploadJob(failedJob.uploadSessionId);
        } catch (abortError) {
          console.error("Failed to notify backend upload abort:", abortError);
        }

        continue;
      }

      await runUploadJob(job);
    } catch (error: any) {
      console.error("Pending upload retry failed:", error);

      const nextAttempts = Math.min(job.attempts + 1, job.maxAttempts);
      const errorMessage = error?.message || "Pending upload retry failed.";

      const nextJob: PendingUploadJob = {
        ...job,
        attempts: nextAttempts,
        status: "failed",
        lastAttemptAtMs: Date.now(),
        lastError: errorMessage,
      };

      upsertPendingUploadJob(nextJob);

      const reachedMaxAttempts = nextAttempts >= nextJob.maxAttempts;

      patchRecording(job.recordingId, {
        uploadStatus: reachedMaxAttempts ? "failed" : "queued",
        errorMessage,
      });

      if (reachedMaxAttempts) {
        try {
          await abortUpload(nextJob, errorMessage);
          removePendingUploadJob(nextJob.uploadSessionId);
        } catch (abortError) {
          console.error("Failed to notify backend upload abort:", abortError);
        }
      }
    }
  }
};

const abortUpload = async (
  job: PendingUploadJob,
  errorMessage: string
) => {
  const response = await fetch(
    buildApiUrl(
      apiBaseUrl,
      `/audio-recording/upload-abort/${job.uploadSessionId}`
    ),
    {
      method: "POST",
      credentials: "omit",
      headers: getRequestHeaders(true),
      body: JSON.stringify({
        consent_bundle_id: job.consentBundleId,
        upload_session_id: job.uploadSessionId,
        recording_id: job.recordingId,
        file_name: job.fileName,
        mime_type: job.mimeType,
        size: job.size,
        duration_seconds: job.durationSeconds,
        recorded_at: job.recordedAt,
        attempts: job.attempts,
        max_attempts: job.maxAttempts,
        uploaded_parts: job.uploadedParts,
        status: "aborted",
        error_message: errorMessage,
      }),
    }
  );

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(
      `Abort upload failed with status ${response.status}${
        message ? `: ${message}` : ""
      }`
    );
  }

  return response.json().catch(() => null);
};

  const handleUploadSelected = async () => {
    if (!selectedRecording) {
      setStatus("Select a saved recording first.");
      return;
    }

    await uploadRecordingWithPresignedUrls(selectedRecording);
  };

  const closeConfirmModal = () => setConfirmModal(null);

  const askToPauseRecording = () => {
    setConfirmModal({
      action: "pause",
      title: "Pause recording?",
      message:
        "The recording will pause now. You can resume it later without losing the current audio.",
      confirmLabel: "Pause for now",
      cancelLabel: "No, keep recording",
    });
  };

  const askToResumeRecording = () => {
    setConfirmModal({
      action: "resume",
      title: "Resume recording?",
      message: "The recorder will continue from where it was paused.",
      confirmLabel: "Yes, resume",
      cancelLabel: "No, keep paused",
    });
  };

  const askToStopRecording = () => {
    setConfirmModal({
      action: "stop",
      title: "Stop and save recording?",
      message:
        "This will save one complete recording locally. You can then upload it through presigned URLs.",
      confirmLabel: "Yes, stop and save",
      cancelLabel: "No, continue recording",
      danger: true,
    });
  };

  const askToDeleteRecording = (id: string) => {
    setConfirmModal({
      action: "delete",
      title: "Delete this recording?",
      message:
        "This saved recording will be removed from local storage. This action cannot be undone.",
      confirmLabel: "Yes, delete",
      cancelLabel: "No, keep it",
      danger: true,
      recordingId: id,
    });
  };

  const askToClearAllRecordings = () => {
    setConfirmModal({
      action: "clearAll",
      title: "Clear all saved recordings?",
      message:
        "All saved local recordings and pending upload sessions will be deleted. This action cannot be undone.",
      confirmLabel: "Yes, clear all",
      cancelLabel: "No, keep recordings",
      danger: true,
    });
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("Audio recording is not supported in this browser");
        return;
      }

      if (!window.MediaRecorder) {
        setStatus("MediaRecorder is not supported in this browser");
        return;
      }

      stopPreviewStream();
      stopMicLevelMonitor();
      fullRecordingPartsRef.current = [];
      setSavedFlash(false);
      setIsPaused(false);
      resetTimer();
      setStatus("Requesting microphone permission...");

      const stream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints()
      );

      mediaStreamRef.current = stream;
      startMicLevelMonitor(stream);
      setIsMicPreviewActive(true);

      const mimeType = getBestMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, {
            mimeType,
            audioBitsPerSecond: 128000,
          })
        : new MediaRecorder(stream, {
            audioBitsPerSecond: 128000,
          });

      mediaRecorderRef.current = recorder;

      recorder.onstart = () => {
        setIsRecording(true);
        setIsPaused(false);
        setStatus("Recording full session locally...");
        startTimer();
      };

      recorder.onpause = () => {
        setIsPaused(true);
        setStatus("Recording paused. Live mic level is still active.");
        stopTimer();
      };

      recorder.onresume = () => {
        setIsPaused(false);
        setStatus("Recording resumed...");
        startTimer();
      };

      recorder.ondataavailable = (event: BlobEvent) => {
        if (!event.data || event.data.size === 0) return;
        fullRecordingPartsRef.current.push(event.data);
      };

      recorder.onerror = (event) => {
        console.error("Recorder error:", event);
        setStatus("Recording error");
        setIsRecording(false);
        setIsPaused(false);
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);
        window.setTimeout(() => startMicPreview(), 250);
      };

      recorder.onstop = async () => {
        stopTimer();
        stopMicLevelMonitor();
        setIsMicPreviewActive(false);

        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        const finalMimeType =
          recorder.mimeType || fullRecordingPartsRef.current[0]?.type || "audio/webm";
        const fullAudioBlob = new Blob(fullRecordingPartsRef.current, {
          type: finalMimeType,
        });

        if (!fullAudioBlob.size || fullRecordingPartsRef.current.length === 0) {
          setStatus("No audio was recorded");
          setIsRecording(false);
          setIsPaused(false);
          window.setTimeout(() => startMicPreview(), 250);
          return;
        }

        try {
          await addSavedRecordingFromBlob(fullAudioBlob, {
            durationSeconds: recordingSecondsRef.current,
          });
        } catch (error: any) {
          console.error(error);
          setStatus(error?.message || "Failed to save recording locally");
        }

        setIsRecording(false);
        setIsPaused(false);
        fullRecordingPartsRef.current = [];
        window.setTimeout(() => startMicPreview(), 250);
      };

      recorder.start();
    } catch (error) {
      console.error(error);
      setStatus("Microphone permission denied or unavailable");
      setIsRecording(false);
      setIsPaused(false);
      setIsMicPreviewActive(false);
      stopTimer();
      stopMicLevelMonitor();
      window.setTimeout(() => startMicPreview(), 250);
    }
  };

  const performPauseRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording") recorder.pause();
  };

  const performResumeRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "paused") recorder.resume();
  };

  const performStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state === "recording" || recorder?.state === "paused") {
      setStatus("Stopping and saving full recording...");
      recorder.stop();
    }
  };

  const stopAllSavedPlayers = () => {
    Object.values(audioRefs.current).forEach((audio) => audio?.pause());
    setActivePlaybackId(null);
  };

  const performDeleteRecording = async (id: string) => {
    const audio = audioRefs.current[id];

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    delete audioRefs.current[id];
    await deleteBlobFromDb(id).catch(() => undefined);

    const deletedRecording = recordings.find((recording) => recording.id === id);
    if (deletedRecording?.objectUrl) URL.revokeObjectURL(deletedRecording.objectUrl);

    const updatedRecordings = recordings.filter((item) => item.id !== id);
    syncRecordings(updatedRecordings);

    const remainingJobs = getPendingUploadJobs().filter((job) => job.recordingId !== id);
    savePendingUploadJobs(remainingJobs);

    setStatus("Recording deleted");

    setPlayerStates((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (activePlaybackId === id) setActivePlaybackId(null);
    if (selectedRecordingId === id) setSelectedRecordingId(updatedRecordings[0]?.id || "");
  };

  const performClearAllRecordings = async () => {
    stopAllSavedPlayers();
    audioRefs.current = {};

    await Promise.all(
      recordings.map(async (recording) => {
        await deleteBlobFromDb(recording.id).catch(() => undefined);
        if (recording.objectUrl) URL.revokeObjectURL(recording.objectUrl);
      })
    );

    localStorage.removeItem(META_STORAGE_KEY);
    localStorage.removeItem(PENDING_UPLOADS_KEY);
    setRecordings([]);
    setSelectedRecordingId("");
    setPlayerStates({});
    setStatus("All recordings cleared");
  };

  const handlePauseFromConfirmModal = () => {
    closeConfirmModal();
    performPauseRecording();
  };

  const handleConfirmAction = () => {
    if (!confirmModal) return;

    const { action, recordingId } = confirmModal;
    closeConfirmModal();

    switch (action) {
      case "pause":
        performPauseRecording();
        break;
      case "resume":
        performResumeRecording();
        break;
      case "stop":
        performStopRecording();
        break;
      case "delete":
        if (recordingId) void performDeleteRecording(recordingId);
        break;
      case "clearAll":
        void performClearAllRecordings();
        break;
      default:
        break;
    }
  };

  const updatePlayerState = (id: string, nextState: Partial<PlayerState>) => {
    setPlayerStates((prev) => ({
      ...prev,
      [id]: {
        currentTime: prev[id]?.currentTime || 0,
        duration: prev[id]?.duration || 0,
        ...nextState,
      },
    }));
  };

  const handleLoadedMetadata = (id: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handleTimeUpdate = (id: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    updatePlayerState(id, {
      currentTime: audio.currentTime || 0,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handlePlayerEnded = (id: string) => {
    const audio = audioRefs.current[id];
    if (audio) audio.currentTime = 0;
    updatePlayerState(id, { currentTime: 0 });
    if (activePlaybackId === id) setActivePlaybackId(null);
  };

  const handleTogglePlayback = async (id: string) => {
    const selectedAudio = audioRefs.current[id];
    if (!selectedAudio) return;

    if (activePlaybackId === id && !selectedAudio.paused) {
      selectedAudio.pause();
      setActivePlaybackId(null);
      return;
    }

    Object.entries(audioRefs.current).forEach(([recordingId, audio]) => {
      if (!audio || recordingId === id) return;
      audio.pause();
    });

    try {
      await selectedAudio.play();
      setActivePlaybackId(id);
    } catch (error) {
      console.error("Failed to play recording:", error);
      setStatus("Could not play this recording");
    }
  };

  const handleSeekRecording = (id: string, value: string) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    const nextTime = Number(value);
    audio.currentTime = nextTime;

    updatePlayerState(id, {
      currentTime: nextTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
  };

  const handleSelectRecording = (id: string) => {
    setSelectedRecordingId(id);
    setStatus("Recording selected. Ready to upload.");
  };

  const handleDroppedFiles = async (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      setStatus("Please upload an audio file.");
      return;
    }

    try {
      setStatus("Saving uploaded audio locally...");
      await addSavedRecordingFromBlob(file, {
        fileName: file.name,
        durationSeconds: await getAudioDurationSeconds(file),
      });
    } catch (error: any) {
      console.error(error);
      setStatus(error?.message || "Failed to add uploaded audio.");
    }
  };

  useEffect(() => {
    let isMounted = true;

    const hydrateRecordings = async () => {
      const storedMeta = getStoredRecordingMeta().slice(0, maxRecordings);
      const hydrated: SavedRecording[] = [];

      for (const meta of storedMeta) {
        const blob = await getBlobFromDb(meta.id).catch(() => null);
        if (!blob) continue;

        const objectUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.push(objectUrl);
        hydrated.push({ ...meta, objectUrl });
      }

      if (!isMounted) return;
      setRecordings(hydrated);
      setSelectedRecordingId(hydrated[0]?.id || "");
    };

    void hydrateRecordings();
    void loadAudioInputDevices();
    void retryPendingUploads();

    retryTimerRef.current = window.setInterval(() => {
      void retryPendingUploads();
    }, retryPollMs);

    const handleDeviceChange = () => void loadAudioInputDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", handleDeviceChange);

    return () => {
      isMounted = false;
      stopTimer();
      stopMicLevelMonitor();
      stopPreviewStream();

      if (retryTimerRef.current) window.clearInterval(retryTimerRef.current);

      navigator.mediaDevices?.removeEventListener?.(
        "devicechange",
        handleDeviceChange
      );

      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());

      Object.values(audioRefs.current).forEach((audio) => audio?.pause());
      audioRefs.current = {};
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="audio-recorder">
      <div
        className={`audio-recorder__card ${
          isRecording ? "audio-recorder__card--recording" : ""
        } ${isPaused ? "audio-recorder__card--paused" : ""} ${
          savedFlash ? "audio-recorder__card--saved" : ""
        }`}
      >
        <div className="audio-recorder__top">
          <div>
            <p className="audio-recorder__eyebrow">Presigned multipart recorder</p>
            <h2>Audio Recorder</h2>
            <p className="audio-recorder__subtitle">
              Record or drop an audio file, request presigned URLs by file size,
              upload sliced parts, then complete the upload session.
            </p>
          </div>

          <button
            type="button"
            className={`audio-recorder__settingsButton ${
              showMicSettings ? "audio-recorder__settingsButton--open" : ""
            }`}
            onClick={() => setShowMicSettings((prev) => !prev)}
            disabled={isRecording}
            aria-label="Open microphone settings"
            title="Microphone settings"
          >
            <span>⚙</span>
          </button>
        </div>

        <div
          className={`audio-recorder__dropBox ${
            isDraggingFile ? "audio-recorder__dropBox--dragging" : ""
          }`}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDraggingFile(true);
          }}
          onDragLeave={() => setIsDraggingFile(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDraggingFile(false);
            void handleDroppedFiles(event.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            hidden
            onChange={(event) => {
              if (event.target.files) void handleDroppedFiles(event.target.files);
              event.currentTarget.value = "";
            }}
          />
          <strong>Drop audio file here</strong>
          <span>or click to select an existing audio file</span>
        </div>

        <div className="audio-recorder__scrollArea">
          <div className="audio-recorder__stickyControls">
            <div className="audio-recorder__timerWrap">
              <div className="audio-recorder__timer">
                <div
                  className={`audio-recorder__recordIcon ${
                    isRecording && !isPaused
                      ? "audio-recorder__recordIcon--active"
                      : ""
                  } ${isPaused ? "audio-recorder__recordIcon--paused" : ""} ${
                    savedFlash ? "audio-recorder__recordIcon--saved" : ""
                  }`}
                >
                  {savedFlash ? "✓" : isPaused ? "Ⅱ" : ""}
                </div>

                <span>{formatTime(recordingSeconds)}</span>
                <small>{status}</small>
              </div>

              <div
                className={`audio-recorder__minimalLevel audio-recorder__minimalLevel--live ${
                  isMicPreviewActive ? "audio-recorder__minimalLevel--active" : ""
                } ${hasNoMicInput ? "audio-recorder__minimalLevel--empty" : ""}`}
              >
                <div className="audio-recorder__minimalLevelTop">
                  <span>
                    {hasLiveInput
                      ? isPaused
                        ? "Live mic input while paused"
                        : isRecording
                        ? "Live mic input while recording"
                        : "Live mic input preview active"
                      : hasNoMicInput
                      ? "No mic input detected"
                      : "Live mic preview starting"}
                  </span>
                  <strong>{isMicPreviewActive ? `${micLevel}%` : selectedMicLabel}</strong>
                </div>

                <div
                  className={`audio-recorder__liveBars ${
                    hasLiveInput
                      ? "audio-recorder__liveBars--hasInput"
                      : "audio-recorder__liveBars--idle"
                  }`}
                  aria-hidden="true"
                >
                  {Array.from({ length: 33 }).map((_, index) => {
                    const centerIndex = 16;
                    const distanceFromCenter = Math.abs(index - centerIndex);
                    const maxDistance = centerIndex;
                    const centerStrength = 1 - distanceFromCenter / maxDistance;
                    const idlePulse =
                      10 +
                      Math.sin(Date.now() / 260 + index * 0.45) * 4 +
                      centerStrength * 10;
                    const activeBoost = hasLiveInput
                      ? micLevel * centerStrength * 0.55
                      : 0;
                    const dynamicHeight = Math.max(
                      8,
                      Math.min(58, idlePulse + activeBoost)
                    );

                    return (
                      <i
                        key={index}
                        className={
                          hasLiveInput && dynamicHeight > 18
                            ? "audio-recorder__liveBar--lit"
                            : ""
                        }
                        style={{ height: `${dynamicHeight}px` }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="audio-recorder__actions audio-recorder__actions--player">
              <div className="audio-recorder__transportSpacer">
                <span>{selectedMicLabel}</span>
              </div>

              <div className="audio-recorder__transportControls">
                {!isRecording && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--record"
                    onClick={startRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--record" />
                    <span>Record</span>
                  </button>
                )}

                {isRecording && !isPaused && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--pause"
                    onClick={askToPauseRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--pause">
                      <i />
                      <i />
                    </i>
                    <span>Pause</span>
                  </button>
                )}

                {isRecording && isPaused && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--resume"
                    onClick={askToResumeRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--play" />
                    <span>Resume</span>
                  </button>
                )}

                {isRecording && (
                  <button
                    type="button"
                    className="audio-recorder__transportButton audio-recorder__transportButton--stop"
                    onClick={askToStopRecording}
                  >
                    <i className="audio-recorder__transportIcon audio-recorder__transportIcon--stop" />
                    <span>Stop</span>
                  </button>
                )}
              </div>

              <button
                type="button"
                className="audio-recorder__clearSavedButton"
                onClick={askToClearAllRecordings}
                disabled={recordings.length === 0 || isRecording}
              >
                Clear saved
              </button>
            </div>
          </div>

          <div
            className={`audio-recorder__micDrawer ${
              showMicSettings ? "audio-recorder__micDrawer--open" : ""
            }`}
          >
            <div className="audio-recorder__micHeader">
              <div>
                <strong>Microphone</strong>
                <span>Select the input device before starting a recording.</span>
              </div>

              <button
                type="button"
                className="audio-recorder__refreshMic"
                onClick={loadAudioInputDevices}
                disabled={isRecording}
              >
                Refresh
              </button>
            </div>

            <div className="audio-recorder__micGrid">
              {audioInputs.length === 0 && (
                <div className="audio-recorder__micEmpty">No microphones found.</div>
              )}

              {audioInputs.map((mic) => (
                <button
                  type="button"
                  key={mic.deviceId}
                  className={`audio-recorder__micOption ${
                    selectedMicId === mic.deviceId
                      ? "audio-recorder__micOption--active"
                      : ""
                  }`}
                  onClick={() => handleMicChange(mic.deviceId)}
                  disabled={isRecording}
                >
                  <span className="audio-recorder__micDot" />
                  <span className="audio-recorder__micName">{mic.label}</span>
                  <span className="audio-recorder__micCheck">
                    {selectedMicId === mic.deviceId ? "Selected" : "Use"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="audio-recorder__savedRegion">
            <div className="audio-recorder__listHeader audio-recorder__listHeader--saved">
              <div>
                <h3>Saved recordings</h3>
                <span>
                  {recordings.length}/{maxRecordings} saved locally
                </span>
              </div>

              <button
                type="button"
                className="audio-recorder__primaryUseButton"
                onClick={handleUploadSelected}
                disabled={
                  !selectedRecording ||
                  isRecording ||
                  selectedRecording.uploadStatus === "uploading"
                }
              >
                {selectedRecording?.uploadStatus === "uploading"
                  ? `Uploading ${selectedRecording.uploadProgress || 0}%`
                  : "Upload audio"}
              </button>
            </div>

            {recordings.length === 0 ? (
              <div className="audio-recorder__empty">
                <strong>No saved recordings yet</strong>
                <p>
                  Press Record or drop an audio file. Then select the saved audio
                  and upload it using presigned URLs.
                </p>
              </div>
            ) : (
              <div className="audio-recorder__list">
                {recordings.map((recording) => {
                  const isSelected = selectedRecordingId === recording.id;
                  const isPlaying = activePlaybackId === recording.id;
                  const playerState = playerStates[recording.id] || {
                    currentTime: 0,
                    duration: recording.durationSeconds || 0,
                  };
                  const duration = playerState.duration || recording.durationSeconds || 0;
                  const progress = duration
                    ? Math.min(100, (playerState.currentTime / duration) * 100)
                    : 0;

                  return (
                    <div
                      key={recording.id}
                      className={`audio-recorder__item audio-recorder__item--modern ${
                        isPlaying ? "audio-recorder__item--playing" : ""
                      } ${isSelected ? "audio-recorder__item--selected" : ""}`}
                    >
                      <div className="audio-recorder__modernHeader">
                        <label className="audio-recorder__selectRow">
                          <input
                            type="radio"
                            name="selected-recording"
                            checked={isSelected}
                            onChange={() => handleSelectRecording(recording.id)}
                          />
                          <span>Select this recording</span>
                        </label>

                        <span className="audio-recorder__playingBadge">
                          {recording.uploadStatus === "uploaded"
                            ? "Uploaded"
                            : recording.uploadStatus === "uploading"
                            ? `Uploading ${recording.uploadProgress || 0}%`
                            : recording.uploadStatus === "queued"
                            ? "Retry queued"
                            : recording.uploadStatus === "failed"
                            ? "Failed"
                            : recording.uploadStatus === "expired"
                            ? "Expired"
                            : isSelected
                            ? "Selected"
                            : isPlaying
                            ? "Playing"
                            : "Saved"}
                        </span>
                      </div>

                      <div className="audio-recorder__modernTitleBlock">
                        <strong>{recording.name}</strong>
                        <div className="audio-recorder__meta audio-recorder__meta--modern">
                          <span>{recording.durationText}</span>
                          <span>{getReadableFileSize(recording.size)}</span>
                          <span>{recording.mimeType || "audio/webm"}</span>
                        </div>
                      </div>

                      {(recording.uploadStatus === "uploading" ||
                        recording.uploadStatus === "queued" ||
                        recording.uploadStatus === "uploaded") && (
                        <div className="audio-recorder__uploadProgress">
                          <div
                            style={{ width: `${recording.uploadProgress || 0}%` }}
                          />
                        </div>
                      )}

                      {recording.errorMessage && (
                        <div className="audio-recorder__uploadError">
                          {recording.errorMessage}
                        </div>
                      )}

                      <div className="audio-recorder__modernPlayer">
                        <button
                          type="button"
                          className={`audio-recorder__playButton ${
                            isPlaying ? "audio-recorder__playButton--active" : ""
                          }`}
                          onClick={() => handleTogglePlayback(recording.id)}
                          aria-label={isPlaying ? "Pause recording" : "Play recording"}
                        >
                          {isPlaying ? (
                            <span className="audio-recorder__pauseIcon">
                              <i />
                              <i />
                            </span>
                          ) : (
                            <span className="audio-recorder__playIcon" />
                          )}
                        </button>

                        <div className="audio-recorder__seekWrap">
                          <div className="audio-recorder__timeRow">
                            <span>{formatTime(playerState.currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>

                          <div className="audio-recorder__seekShell">
                            <div
                              className="audio-recorder__seekFill"
                              style={{ width: `${progress}%` }}
                            />

                            <input
                              className="audio-recorder__seekInput"
                              type="range"
                              min="0"
                              max={duration || 0}
                              step="0.01"
                              value={playerState.currentTime}
                              onChange={(event) =>
                                handleSeekRecording(recording.id, event.target.value)
                              }
                              aria-label="Seek recording"
                            />
                          </div>
                        </div>

                        <audio
                          ref={(audio) => {
                            audioRefs.current[recording.id] = audio;
                          }}
                          src={recording.objectUrl}
                          preload="metadata"
                          onLoadedMetadata={() => handleLoadedMetadata(recording.id)}
                          onTimeUpdate={() => handleTimeUpdate(recording.id)}
                          onEnded={() => handlePlayerEnded(recording.id)}
                        />
                      </div>

                      <div className="audio-recorder__itemActions audio-recorder__itemActions--modern">
                        <a
                          className="audio-recorder__download"
                          href={recording.objectUrl}
                          download={recording.name}
                        >
                          Download
                        </a>

                        <button
                          type="button"
                          className="audio-recorder__delete"
                          onClick={() => askToDeleteRecording(recording.id)}
                          disabled={isRecording || recording.uploadStatus === "uploading"}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmModal && (
        <div
          className="audio-recorder__modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="audio-recorder-confirm-title"
        >
          <div className="audio-recorder__confirmModal">
            <div
              className={`audio-recorder__confirmIcon ${
                confirmModal.danger ? "audio-recorder__confirmIcon--danger" : ""
              }`}
            >
              {confirmModal.danger ? "!" : "?"}
            </div>

            <div className="audio-recorder__confirmContent">
              <h3 id="audio-recorder-confirm-title">{confirmModal.title}</h3>
              <p>{confirmModal.message}</p>
            </div>

            <div className="audio-recorder__confirmActions">
              <button
                type="button"
                className="audio-recorder__confirmCancel"
                onClick={closeConfirmModal}
              >
                {confirmModal.cancelLabel || "No"}
              </button>

              {confirmModal.action === "stop" && isRecording && !isPaused && (
                <button
                  type="button"
                  className="audio-recorder__confirmPause"
                  onClick={handlePauseFromConfirmModal}
                >
                  Pause for now
                </button>
              )}

              <button
                type="button"
                className={`audio-recorder__confirmSubmit ${
                  confirmModal.danger
                    ? "audio-recorder__confirmSubmit--danger"
                    : ""
                }`}
                onClick={handleConfirmAction}
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;